import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { FunctionDefinition } from '../../core/api/models/function-definition.model';
import { FunctionQueueStatus, FunctionRun, FunctionRunLog } from '../../core/api/models/function-run.model';
import { PagedResult } from '../../core/api/models/pagination.model';
import { UserImportRecord } from '../../core/api/models/user-import.model';
import { FunctionRunsApiService } from '../../core/api/services/function-runs-api.service';
import { SyncServicesApiService } from '../../core/api/services/sync-services-api.service';
import { PaginationChange, PaginationComponent } from '../../shared/ui/pagination/pagination.component';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../shared/ui/pagination/pagination.config';
import { formatDurationMilliseconds, formatRunDuration } from '../../shared/ui/duration/duration';

@Component({
  selector: 'app-function-run-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, PaginationComponent],
  templateUrl: './function-run-detail.component.html',
  styleUrl: './function-run-detail.component.scss',
})
export class FunctionRunDetailComponent implements OnInit {
  private readonly runsApi = inject(FunctionRunsApiService);
  private readonly syncServicesApi = inject(SyncServicesApiService);
  private readonly route = inject(ActivatedRoute);

  run: FunctionRun | null = null;
  definition: FunctionDefinition | null = null;
  users: PagedResult<UserImportRecord> | null = null;
  logs: FunctionRunLog[] = [];
  usersPaging: PaginationChange = { page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE };
  usersLoading = false;
  logsLoading = false;
  error = '';
  usersError = '';
  logsError = '';
  private runId = 0;

  ngOnInit(): void {
    this.runId = Number(this.route.snapshot.paramMap.get('runId'));
    if (!Number.isSafeInteger(this.runId) || this.runId < 1) {
      this.error = 'The run reference is invalid.';
      return;
    }
    this.runsApi.getRun(this.runId).subscribe({
      next: value => {
        this.run = value;
        this.loadDefinition(value.functionName);
      },
      error: () => this.error = 'Could not load run details.',
    });
    this.loadLogs();
    this.loadUsers();
  }

  loadLogs(): void {
    this.logsLoading = true;
    this.logsError = '';
    this.runsApi.getRunLogs(this.runId).pipe(finalize(() => this.logsLoading = false)).subscribe({
      next: value => this.logs = value,
      error: () => this.logsError = 'Could not load execution logs.',
    });
  }

  changeUsersPagination(paging: PaginationChange): void {
    this.usersPaging = paging;
    this.loadUsers();
  }

  roles(values: string[]): string {
    return values.length ? values.join(', ') : '—';
  }

  statusClass(status: FunctionQueueStatus): string {
    return `status ${status.toLowerCase()}`;
  }

  timeoutLabel(timeoutMilliseconds: number | undefined): string {
    return timeoutMilliseconds && timeoutMilliseconds > 0
      ? formatDurationMilliseconds(timeoutMilliseconds)
      : 'Unavailable';
  }

  duration(run: FunctionRun): string {
    return formatRunDuration(run.startedAtUtc, run.completedAtUtc);
  }

  private loadDefinition(functionName: string): void {
    this.syncServicesApi.getDefinition(functionName).subscribe({
      next: value => this.definition = value,
      error: () => this.definition = null,
    });
  }

  private loadUsers(): void {
    this.usersLoading = true;
    this.usersError = '';
    this.runsApi.getRunUsers(this.runId, this.usersPaging.page, this.usersPaging.pageSize)
      .pipe(finalize(() => this.usersLoading = false))
      .subscribe({
        next: value => {
          this.users = value;
          this.usersPaging = { page: value.page, pageSize: value.pageSize };
        },
        error: () => this.usersError = 'Could not load changed users.',
      });
  }
}
